import * as THREE from "three";
import { surfaceY } from "./views";

interface Particle { life: number; maxLife: number; x: number; y: number; z: number; vx: number; vy: number; vz: number; size: number; r: number; g: number; b: number; gravity: number }
interface Projectile { from: THREE.Vector3; to: THREE.Vector3; t: number; duration: number; arc: number; mesh: THREE.Mesh; onHit?: () => void }

/**
 * The built-in effects (effects.ts EffectsView draws them when the sprite VFX has not loaded, or for what it leaves
 * out): additive point particles and simple rounds (arrows, cannonballs), pooled.
 */
export class ParticlePool {
  private readonly max = 1200;
  private readonly particles: Particle[] = [];
  private readonly positions: Float32Array;
  private readonly colors: Float32Array;
  private readonly sizes: Float32Array;
  private readonly points: THREE.Points;
  private readonly projectiles: Projectile[] = [];
  private readonly projectilePool: THREE.Mesh[] = [];
  readonly ballGeo = new THREE.SphereGeometry(0.09, 6, 6);
  readonly arrowGeo = new THREE.ConeGeometry(0.05, 0.35, 5);
  readonly ballMat = new THREE.MeshBasicMaterial({ color: 0x2b2b2b });
  readonly arrowMat = new THREE.MeshBasicMaterial({ color: 0xf4e7c3 });

  constructor(private readonly group: THREE.Group) {
    this.positions = new Float32Array(this.max * 3);
    this.colors = new Float32Array(this.max * 3);
    this.sizes = new Float32Array(this.max);
    const geo = new THREE.BufferGeometry();
    geo.setAttribute("position", new THREE.BufferAttribute(this.positions, 3).setUsage(THREE.DynamicDrawUsage));
    geo.setAttribute("color", new THREE.BufferAttribute(this.colors, 3).setUsage(THREE.DynamicDrawUsage));
    geo.setAttribute("size", new THREE.BufferAttribute(this.sizes, 1).setUsage(THREE.DynamicDrawUsage));
    const mat = new THREE.ShaderMaterial({
      transparent: true, depthWrite: false, blending: THREE.AdditiveBlending, vertexColors: true,
      uniforms: { scale: { value: 300 } },
      vertexShader: `attribute float size; varying vec3 vColor; uniform float scale; void main(){ vColor = color; vec4 mv = modelViewMatrix * vec4(position, 1.0); gl_PointSize = size * scale / -mv.z; gl_Position = projectionMatrix * mv; }`,
      fragmentShader: `varying vec3 vColor; void main(){ vec2 d = gl_PointCoord - 0.5; float a = smoothstep(0.5, 0.05, length(d)); gl_FragColor = vec4(vColor * a, a); }`,
    });
    this.points = new THREE.Points(geo, mat);
    this.points.frustumCulled = false;
    this.group.add(this.points);
  }

  /** Moves every particle and round on by `dt` and uploads what is live. */
  step(dt: number): void {
    const floor = surfaceY() + 0.02;
    for (let i = this.particles.length - 1; i >= 0; i--) {
      const q = this.particles[i];
      q.life -= dt;
      if (q.life <= 0) { this.particles[i] = this.particles[this.particles.length - 1]; this.particles.pop(); this.spare.push(q); continue; }
      q.vy -= q.gravity * dt;
      q.x += q.vx * dt; q.y += q.vy * dt; q.z += q.vz * dt;
      if (q.y < floor) { q.y = floor; q.vy = -q.vy * 0.3; q.vx *= 0.7; q.vz *= 0.7; }
    }
    let n = 0;
    for (const q of this.particles) {
      if (n >= this.max) break;
      const k = q.life / q.maxLife;
      this.positions[n * 3] = q.x; this.positions[n * 3 + 1] = q.y; this.positions[n * 3 + 2] = q.z;
      this.colors[n * 3] = q.r * k; this.colors[n * 3 + 1] = q.g * k; this.colors[n * 3 + 2] = q.b * k;
      this.sizes[n] = q.size * (0.5 + k);
      n++;
    }
    const geo = this.points.geometry;
    // Upload only the live particles, and nothing at all while there are none: three.js otherwise re-sends all
    // 1200 slots of all three attributes every frame.
    if (n > 0 || this.drawn > 0) {
      for (const [name, size] of ParticlePool.Attributes) {
        const a = geo.attributes[name] as THREE.BufferAttribute;
        a.clearUpdateRanges();
        if (n > 0) { a.addUpdateRange(0, n * size); a.needsUpdate = true; }
      }
    }
    this.drawn = n;
    geo.setDrawRange(0, n);
    for (let i = this.projectiles.length - 1; i >= 0; i--) {
      const p = this.projectiles[i];
      p.t += dt / p.duration;
      if (p.t >= 1) { p.onHit?.(); p.mesh.visible = false; this.projectilePool.push(p.mesh); this.projectiles.splice(i, 1); continue; }
      p.mesh.position.lerpVectors(p.from, p.to, p.t);
      p.mesh.position.y += Math.sin(p.t * Math.PI) * p.arc;
      p.mesh.quaternion.setFromUnitVectors(THREE.Object3D.DEFAULT_UP, ParticlePool.dir.copy(p.to).sub(p.from).normalize());
    }
  }
  /** Particles written last frame, so an empty system stops uploading after the frame that emptied it. */
  private drawn = 0;
  private static readonly dir = new THREE.Vector3();
  /** The streamed attributes and their floats per particle. */
  private static readonly Attributes: readonly (readonly [string, number])[] = [["position", 3], ["color", 3], ["size", 1]];

  dispose(): void {
    this.points.geometry.dispose(); (this.points.material as THREE.Material).dispose();
    for (const g of [this.ballGeo, this.arrowGeo]) g.dispose();
    for (const m of [this.ballMat, this.arrowMat]) m.dispose();
  }

  shoot(from: THREE.Vector3, to: THREE.Vector3, duration: number, arc: number, geo: THREE.BufferGeometry, mat: THREE.Material, onHit?: () => void): void {
    let mesh = this.projectilePool.pop();
    if (!mesh) { mesh = new THREE.Mesh(geo, mat); this.group.add(mesh); }
    mesh.geometry = geo; mesh.material = mat; mesh.visible = true; mesh.position.copy(from);
    this.projectiles.push({ from: from.clone(), to: to.clone(), t: 0, duration, arc, mesh, onHit });
  }

  spark(from: THREE.Vector3, to: THREE.Vector3): void {
    const steps = 10;
    for (let i = 0; i <= steps; i++) {
      const k = i / steps;
      const jitter = i === 0 || i === steps ? 0 : 0.15;
      this.emit(from.x + (to.x - from.x) * k + (Math.random() - 0.5) * jitter, from.y + (to.y - from.y) * k + (Math.random() - 0.5) * jitter, from.z + (to.z - from.z) * k + (Math.random() - 0.5) * jitter, 0, 0, 0, 0.09, 0.9, 0.95, 0.5, 0.18, 0);
    }
  }

  burst(at: THREE.Vector3, count: number, r: number, g: number, b: number, size: number, speed: number, life = 0.6): void {
    for (let i = 0; i < count; i++) {
      const a = Math.random() * Math.PI * 2, u = Math.random();
      const s = speed * (0.4 + Math.random() * 0.8);
      this.emit(at.x, at.y, at.z, Math.cos(a) * s * u, s * (0.5 + Math.random() * 0.8), Math.sin(a) * s * u, size * (0.7 + Math.random() * 0.6), r, g, b, life * (0.6 + Math.random() * 0.8), 4.5);
    }
  }

  emit(x: number, y: number, z: number, vx: number, vy: number, vz: number, size: number, r: number, g: number, b: number, life: number, gravity: number): void {
    if (this.particles.length >= this.max) return;
    // Dead particles are recycled rather than dropped for the collector: a battle emits hundreds a second.
    const q = this.spare.pop();
    if (!q) { this.particles.push({ life, maxLife: life, x, y, z, vx, vy, vz, size, r, g, b, gravity }); return; }
    q.life = life; q.maxLife = life; q.x = x; q.y = y; q.z = z; q.vx = vx; q.vy = vy; q.vz = vz; q.size = size; q.r = r; q.g = g; q.b = b; q.gravity = gravity;
    this.particles.push(q);
  }
  private readonly spare: Particle[] = [];
}
